/**
 * The typed client for the admin API.
 *
 * ── What replaced 300 lines of the old one ───────────────────────────
 *
 * The portal this succeeds had a client that managed access tokens, refresh
 * tokens, a refresh-on-401 queue, localStorage, and a router deciding which of
 * two backends a path belonged to. Almost all of it existed to work around
 * holding a credential in the browser.
 *
 * There is no credential here. The session cookie is HttpOnly, the browser
 * attaches it, and an expired session is a 401 meaning "sign in again" rather
 * than "try the refresh endpoint and replay the queue". What is left is a fetch
 * wrapper and a list of shapes.
 */
import type { Requirement } from './tester-requirements.js';
import type { TesterDevice } from '../screens/Testers.js';
import { AUTH_URL } from './auth-client.js';
import type { AgreementKind, PublicFields, PublicPatch } from './agreement-public.js';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly code?: string,
    /**
     * The rest of the error body, when the server sent more than a message.
     *
     * Added for branding, whose whole point is per-field detail: a refusal
     * saying "three values are invalid" without naming them leaves the admin
     * exactly as stuck as the silent drop the endpoint exists to replace. Kept
     * generic rather than typed to branding, because the next endpoint that
     * wants to explain itself should not have to change this class.
     */
    readonly detail?: Record<string, unknown>,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * The `code` on an ApiError THIS CLIENT raised, rather than one the server sent.
 *
 * It exists because `describe()` rewrites a 404 into "this account probably
 * lacks the admin role" — which is true of the admin API, where the staff guard
 * answers 404 so the surface is not advertised, and is exactly the wrong thing
 * to say about a lookup that reached the server fine and found no such record.
 * Marking ours keeps that rewrite where it belongs.
 */
export const CLIENT_ERROR = 'org-admin/client';

/**
 * Every admin endpoint lives under `/api/auth`, because they are Better Auth
 * plugin endpoints rather than a separate API.
 *
 * Stated because `/api/auth/admin/users` reads like a mistake until you know the
 * admin surface IS part of the auth service — which is the whole architecture.
 */
const BASE = `${AUTH_URL}/api/auth`;

async function request<T>(path: string, init: RequestInit = {}, base = BASE): Promise<T> {
  const res = await fetch(`${base}${path}`, {
    ...init,
    credentials: 'include',
    headers: { 'content-type': 'application/json', ...(init.headers ?? {}) },
  });

  if (!res.ok) {
    let code: string | undefined;
    let message = `${res.status} ${res.statusText}`;
    let detail: Record<string, unknown> | undefined;
    try {
      const body = (await res.json()) as { message?: string; code?: string } & Record<string, unknown>;
      if (body.message) message = body.message;
      code = body.code;
      detail = body;
    } catch {
      // A non-JSON error body is itself informative — usually a proxy or the
      // static host answering instead of the API.
    }
    throw new ApiError(res.status, message, code, detail);
  }
  return (await res.json()) as T;
}

/**
 * The same request, against the auth service's ORIGIN rather than its auth base.
 *
 * Only for endpoints the Better Auth handler does not own: credential issuing,
 * inviting a user (org-admin#83), and the Notifications reads (org-admin#131). Both are Express routes on the auth
 * service for the same reason — they call `auth.api`, which a Better Auth
 * plugin endpoint cannot without making `auth` reference itself. Kept as a separate object rather than a flag on
 * `request`, so a path that skips `/api/auth` has to say so at the call site
 * instead of a caller passing `false` and nobody noticing which base was used.
 */
async function rootRequest<T>(path: string, init: RequestInit = {}): Promise<T> {
  return request<T>(path, init, AUTH_URL);
}

const rootApi = {
  get: <T>(p: string) => rootRequest<T>(p),
  post: <T>(p: string, body?: unknown) =>
    rootRequest<T>(p, { method: 'POST', body: JSON.stringify(body ?? {}) }),
  patch: <T>(p: string, body: unknown) => rootRequest<T>(p, { method: 'PATCH', body: JSON.stringify(body) }),
  /** A 204 has no body to parse, so this one answers nothing. */
  del: async (p: string): Promise<void> => {
    const res = await fetch(`${AUTH_URL}${p}`, { method: 'DELETE', credentials: 'include' });
    if (!res.ok) {
      const body = (await res.json().catch(() => ({}))) as { message?: string; code?: string };
      throw new ApiError(res.status, body.message ?? `${res.status} ${res.statusText}`, body.code, body);
    }
  },
};

const api = {
  get: <T>(p: string) => request<T>(p),
  post: <T>(p: string, body?: unknown) => request<T>(p, { method: 'POST', body: JSON.stringify(body ?? {}) }),
  patch: <T>(p: string, body: unknown) => request<T>(p, { method: 'PATCH', body: JSON.stringify(body) }),
  put: <T>(p: string, body: unknown) => request<T>(p, { method: 'PUT', body: JSON.stringify(body) }),
  del: <T>(p: string) => request<T>(p, { method: 'DELETE' }),
};

/**
 * Builds `?a=1&b=2`, dropping anything unset — so a caller can pass optional
 * filters straight through without assembling the string at every call site.
 *
 * An ARRAY becomes a repeated parameter: `{ q: ['a', 'b'] }` is `?q=a&q=b`.
 * That is the wire shape the chip search uses, and it is repetition rather than
 * a delimited string because a delimiter would have to be a character no search
 * term can contain, and there is none — somebody eventually searches for a
 * comma. `append`, not `set`, or each term would overwrite the last.
 */
function q(params: Record<string, string | number | boolean | string[] | undefined>): string {
  const s = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === '') continue;
    if (Array.isArray(v)) for (const one of v) s.append(k, one);
    else s.set(k, String(v));
  }
  const out = s.toString();
  return out ? `?${out}` : '';
}

// ── Shapes ───────────────────────────────────────────────────────────
// Hand-written rather than generated. The auth service exposes an OpenAPI
// document (staff-only, `/open-api/generate-schema`), so these could be
// generated later — but that is a build step to maintain, and this is six
// screens' worth of shapes.

/**
 * A named, FLAT set of people that carries platform roles and application
 * entitlements (RidgeWill/docs adr/0006).
 *
 * `protected` is computed by the server rather than stored, because there is no
 * column: `basic-users` and `admins` are named in code by SLUG, so that renaming
 * one cannot un-protect it and no form can clear a boolean that would.
 */
export type SecurityGroup = {
  slug: string;
  name: string;
  description: string;
  members: number;
  roles: string[];
  /** Enough to render a badge. The detail read carries the ids, because that is
   *  where the whole-set write is composed. */
  appRoles: { name: string; label: string; appSlug: string }[];
  applications: string[];
  protected: boolean;
};

/**
 * The detail read, whose app roles carry IDS and the list's do not.
 *
 * `Omit` rather than an intersection: intersecting would leave `appRoles` as
 * both shapes at once, and a caller reading `.id` off it would typecheck here
 * and be `undefined` at runtime on the list.
 *
 * The difference is real rather than an oversight. The write endpoint takes the
 * COMPLETE set across every application, so the detail has to carry enough to
 * compose it; the list only ever renders a badge, and giving it ids would invite
 * a whole-set write composed from a screen that never meant to be authoritative.
 */
export type SecurityGroupDetail = Omit<SecurityGroup, 'appRoles'> & {
  createdAt: string;
  updatedAt: string;
  appRoles: { id: string; name: string; label: string; appSlug: string }[];
};

export type GroupMember = {
  id: string;
  email: string | null;
  name: string | null;
  pseudonymized: boolean;
  /** The EFFECTIVE set off the account, which includes roles from other groups.
   *  It answers "what can this person do", not "what does this membership give
   *  them" — the two differ for anybody in more than one group. */
  role: string | null;
  banned: boolean;
  addedAt: string;
  addedByUserId: string | null;
};

/**
 * Where one entitlement or role on an account came from.
 *
 * A LIST per row, not a value. Somebody can hold the same role directly and
 * through a group at once, and a screen that showed only one source would tell
 * an administrator that removing them from the group withdraws it — which is
 * false in exactly the case that matters.
 */
export type AccessSource =
  | { kind: 'direct' }
  | { kind: 'group'; slug: string; name: string }
  /** Conferred in code rather than by a row: everybody reaches the Account
   *  portal, and the break-glass role reaches the Admin Portal so a
   *  misconfigured group cannot lock the platform out. Nothing to remove. */
  | { kind: 'implied'; reason: 'everybody' | 'break-glass' };

export type AccessRow = { name: string; via: AccessSource[] };

/**
 * What each platform role is called, keyed by the slug.
 *
 * A map alongside the role ARRAYS rather than a change to them: every consumer
 * treats `roles` as the list of strings it must send back, so turning those into
 * objects would break each one at once for a presentation change. Optional here
 * because a portal deployed against an auth that predates it must still render —
 * `roleLabel` falls back to the slug.
 */
export type RoleLabels = Record<string, { label: string; description: string }>;

/**
 * A role belonging to ONE application, whose meaning that application owns.
 *
 * Auth stores the `name` and who holds it and cannot interpret either — so
 * nothing on this platform can tell a typo from a new role. That is why the
 * create form shows the exact string a consumer will match on.
 */
export type AppRoleRow = {
  id: string;
  name: string;
  label: string;
  description: string;
  users: number;
  groups: number;
};

/** One app role a person holds, and where it came from. `break-glass` is its
 *  own kind: Global Admin holds every app role implicitly, with no row. */
export type HeldAppRole = {
  /** Carried because a whole-set write names roles by id — a name is unique
   *  only within its application, so `admin` is not an answer to "which role". */
  id: string;
  name: string;
  label: string;
  appSlug: string;
  via: ({ kind: 'direct' } | { kind: 'group'; slug: string; name: string } | { kind: 'break-glass' })[];
};

/**
 * A subscription as auth resolves it (auth#434): `tier` is the answer, already
 * FREE when entitlement has ended. `status` is ACTIVE, PAST_DUE, EXPIRED, or
 * NONE when there is no row at all.
 */
export type ResolvedSubscription = {
  appSlug: string;
  tier: string;
  status: 'ACTIVE' | 'PAST_DUE' | 'EXPIRED' | 'NONE' | string;
  currentPeriodEnd: string | null;
  endedTier: string | null;
};

export type StaffRef = { id: string; name: string | null; email: string };

/** One row on a person's Subscriptions tab: an app they hold anything in (auth#540, #567). */
export type UserSubscriptionApp = {
  appSlug: string;
  appName: string;
  subscription: ResolvedSubscription;
  /** The storefront; null for a grant in force and whenever it is unknown. */
  store: string | null;
  grant: {
    inForce: boolean;
    grantedAt: string;
    grantedBy: StaffRef | null;
    reason: string | null;
    /** Null on a staff grant; set when auth made it (auth#643). Absent from an older auth. */
    source?: string | null;
  } | null;
  /** Whether THIS caller may end the grant: its own, or anybody's with end-any. */
  canEnd: boolean;
};

/** One change in a person's subscription history, newest first. */
export type SubscriptionHistoryEntry = {
  id: string;
  occurredAt: string;
  /** webhook, reconcile, grant, grant-ended, grant-expired */
  source: string;
  /** The RevenueCat event type, for `webhook`. */
  eventType: string | null;
  fromTier: string | null;
  fromStatus: string | null;
  toTier: string;
  toStatus: string;
  currentPeriodEnd: string | null;
  store: string | null;
  reason: string | null;
  application: { slug: string; name: string };
  actor: StaffRef | null;
};

/** An application staff can grant a tier of: the grant form's app picker. */
export type GrantableApplication = { appSlug: string; appName: string; tiers: string[] };

export type UserSubscriptions = {
  canGrant: boolean;
  /** One row per application the person holds anything in. */
  subscriptions: UserSubscriptionApp[];
  /** Every application with tiers, lowest tier first. */
  grantable: GrantableApplication[];
  history: SubscriptionHistoryEntry[];
};

/** One paid tier an application sells (auth#567). Order in the list is the rank. */
export type ApplicationTier = { id: string; name: string; rank: number; entitlementKey: string };
export type ApplicationTierInput = { id?: string; name: string; entitlementKey: string };

/** What one released version changed, for people (RidgeWill/keyodo#709). */
export type ReleaseNote = {
  id: string;
  /** MAJOR.MINOR.PATCH. */
  version: string;
  /** YYYY-MM-DD, or null on a draft not yet dated. */
  releasedOn: string | null;
  /** Markdown: a lead line and "- " bullets. */
  notes: string;
  /** Changes data collection, the age rating, money or a major feature (keyodo#673). */
  significant: boolean;
  /** For the stores and guardians. Never shown in the app. */
  significantNote: string;
  /** A required release is the app's minimum version: older apps are refused (RidgeWill/auth#680). */
  required: boolean;
  published: boolean;
  publishedAt: string | null;
  updatedAt: string;
  updatedByName: string;
};
export type ReleaseNoteInput = {
  version: string;
  releasedOn: string | null;
  notes: string;
  significant: boolean;
  significantNote: string;
};

/**
 * An application's version gate, worked out from its releases (RidgeWill/auth#680),
 * and how the event that tells the app last went.
 */
export type AppVersions = {
  /** The highest published release. Older apps are prompted to update. */
  latestVersion: string | null;
  /** The highest published Required release. Older apps are refused. */
  minimumVersion: string | null;
  revision: number;
  delivery: {
    status: 'pending' | 'delivered' | 'not-subscribed' | 'unconfigured' | 'abandoned' | null;
    deliveredRevision: number;
    attempts: number;
    lastAttemptAt: string | null;
    deliveredAt: string | null;
    lastError: string | null;
    syncedAt: string | null;
  };
};

/** How one change counts on the Subscriptions chart (org-admin#162). */
export type SubscriptionMovement = 'new' | 'granted' | 'renewed' | 'lapsed' | 'refunded' | 'grantEnded';

export type PersonRef = { id: string; name: string | null; email: string };
type AppRef = { slug: string; name: string };

export type SubscriptionOverview = {
  active: {
    total: number;
    paid: number;
    promotional: number;
    paymentRetrying: number;
    breakdown: { appSlug: string; appName: string; tier: string; source: string; count: number }[];
  };
  /** Oldest first, empty weeks included. `week` is the Monday, UTC. */
  weeks: { week: string; counts: Record<SubscriptionMovement, number> }[];
  /** When the history starts; nothing before it can be charted. */
  historySince: string | null;
  attention: {
    paymentRetrying: { tier: string; currentPeriodEnd: string | null; user: PersonRef; application: AppRef }[];
    grantsEnding: {
      tier: string;
      currentPeriodEnd: string | null;
      grantReason: string | null;
      grantSource?: string | null;
      user: PersonRef;
      grantedBy: StaffRef | null;
      application: AppRef;
    }[];
    renewalOverdue: { tier: string; currentPeriodEnd: string | null; user: PersonRef; application: AppRef }[];
  };
  sync: {
    reconciliationConfigured: boolean;
    lastWebhookAt: string | null;
    webhooksLast24h: number;
    lastLapseSweep: { at: string; ok: boolean; ms: number } | null;
    lastGrantExpiry: { at: string; ok: boolean; ms: number } | null;
  };
};

export type PromotionalGrantRow = {
  tier: string;
  status: string;
  currentPeriodEnd: string | null;
  grantedAt: string;
  grantReason: string | null;
  /** Null on a staff grant; set when auth made it (auth#643). Absent from an older auth. */
  grantSource?: string | null;
  inForce: boolean;
  canEnd: boolean;
  user: PersonRef;
  grantedBy: StaffRef | null;
  application: AppRef;
};

export type SubscriptionEventRow = SubscriptionHistoryEntry & {
  user: PersonRef;
  movement: SubscriptionMovement | null;
};

export type UserAccess = {
  applications: AccessRow[];
  roles: AccessRow[];
  appRoles: HeldAppRole[];
  /** Ids, not names — a name is unique only within its application, so a set of
   *  names is not something a whole-set write could resolve. */
  directAppRoleIds: string[];
  groups: { slug: string; name: string; addedAt: string }[];
  /** What a whole-set write to `/applications` must send back to change
   *  nothing. Sent by the server so a client never derives it by filtering
   *  sources — done wrongly, that pins every inherited entitlement as direct. */
  directApplications: string[];
};

/**
 * The dashboard payload. EVERY SECTION IS NULLABLE (RidgeWill/auth#518): auth
 * sends a section only to a role that holds the permission of the screen that
 * owns it (`users` for `user:list`, `applications` for `application:read`,
 * `agreements` for `agreement:read`, `credentials` for `credential:read`,
 * `signIns` and `recentActivity` for `activity:read`). Before #518 every staff
 * role received all of it, so an auth older than that simply fills every field.
 */
export type Overview = {
  users: {
    total: number;
    verified: number;
    banned: number;
    closed: number;
    /** Two different questions over the same window: how many accounts APPEARED,
     *  and how many were USED. A platform can grow while nobody signs in. */
    newThisWeek: number;
    activeThisWeek: number;
  } | null;
  applications: { total: number; live: number; rows: OverviewApplication[] } | null;
  agreements: { active: number; usersOwing: number } | null;
  credentials: { oauthClients: number; serviceCredentials: number } | null;
  signIns: { last24h: number; failedLast24h: number } | null;
  recentActivity: FeedEvent[] | null;
};

/** `users` is "accounts holding a role that grants this app" — there is no
 *  user-to-application join, because entitlement is per role. */
export type OverviewApplication = {
  id: string;
  slug: string;
  name: string;
  description: string;
  platform: string;
  status: string;
  active: boolean;
  users: number;
};

export type AdminUser = {
  id: string;
  email: string | null;
  name?: string | null;
  /**
   * The avatar's public URL, or null.
   *
   * Optional on the type because a portal deployed against an auth that
   * predates RidgeWill/auth#285 must still render — `Avatar` falls back to the
   * monogram, which is what every account showed before this existed.
   *
   * Null for a CLOSED account, where the server clears it with the rest of the
   * PII. A photograph beside the words "closed account" is the thing
   * pseudonymizing is for.
   */
  image?: string | null;
  role?: string | null;
  emailVerified: boolean;
  banned?: boolean | null;
  banReason?: string | null;
  banExpires?: string | null;
  twoFactorEnabled?: boolean | null;
  /**
   * This account is an automated agent rather than a person
   * (RidgeWill/platform#31).
   *
   * Optional on the type because a portal deployed against an auth that
   * predates RidgeWill/auth#535 does not send it — and the fallback has to be
   * "not an agent", which `?? false` at each use gives. Reading `undefined` as
   * true would badge everybody.
   */
  isAgent?: boolean;
  /**
   * On AGENT rows only (RidgeWill/auth#544): who created or designated it, and
   * the latest use of any of its keys. Absent on a person, and absent from an
   * auth older than #544, which the Agents screen shows as unknown.
   */
  sponsor?: { id: string; name: string | null; email: string | null } | null;
  lastActivityAt?: string | null;
  pseudonymized?: boolean;
  createdAt: string;
  /**
   * The age band from the date of birth on file (org-admin#187), never the date
   * itself. Null with no date on file; absent from an auth that predates it,
   * which reads the same.
   */
  ageBand?: AgeBand | null;
};

export type AgeBand = '13-15' | '16-17' | '18+';

/** Where a date of birth came from (auth: `UserBirthDate.source`). A string, so a new source shows as itself. */
export type BirthDateSource = 'signup' | 'prompt' | 'member' | 'support' | string;

/** One saved change to a member's date of birth, newest first (auth#672). */
export type BirthDateChange = {
  previousDate: string | null;
  newDate: string;
  source: BirthDateSource;
  setById: string | null;
  /** Free text support typed with a correction. Null on every other change. */
  ticketReference: string | null;
  createdAt: string;
};

/** `GET /admin/users/:id/birth-date`. `history` is absent from an auth older than auth#672. */
export type UserBirthDate = {
  dateOfBirth: string | null;
  ageBand: AgeBand | null;
  source: BirthDateSource | null;
  setAt: string | null;
  setById: string | null;
  history?: BirthDateChange[];
};

/** One person as the Family Group read names them. PII null on a closed account. */
export type FamilyPerson = {
  id: string;
  name: string | null;
  email: string | null;
  image: string | null;
  ageBand: AgeBand | null;
};

export type FamilyMember = FamilyPerson & {
  role: 'guardian' | 'dependent' | string;
  joinedAt: string;
  leftAt: string | null;
  leftReason: string | null;
};

export type FamilyAttestation = {
  by: FamilyPerson | null;
  at: string;
  ip: string | null;
  userAgent: string | null;
  city: string | null;
  region: string | null;
  country: string | null;
  statedUnder18: boolean | null;
};

export type FamilyMembership = {
  groupId: string;
  role: 'guardian' | 'dependent' | string;
  joinedAt: string;
  leftAt: string | null;
  leftReason: string | null;
  endedBy: FamilyPerson | null;
  startedTheGroup: boolean;
  group: { id: string; createdAt: string; closedAt: string | null; openInvitations: number; members: FamilyMember[] };
  attestation: FamilyAttestation | null;
  adultAcknowledgedAt: string | null;
};

export type FamilyEvent = {
  at: string;
  kind:
    | 'joined'
    | 'left'
    | 'removed'
    | 'closed-account'
    | 'invitation-declined'
    | 'invitation-refused-minor'
    | 'invitation-refused-adult'
    | 'invited'
    | string;
  groupId: string;
  by: FamilyPerson | null;
  subject: FamilyPerson | null;
  statedUnder18?: boolean | null;
  outcome?: string | null;
};

/** `GET /admin/users/:id/family` (org-admin#187): read-only. */
export type UserFamily = { memberships: FamilyMembership[]; history: FamilyEvent[] };

/**
 * The Agreements filter pills, as one exclusive choice.
 *
 * `org`, `app` and `group` are the three kinds an agreement can be (`group`
 * since auth#613); `retired` cuts across all three. `org` means NEITHER an app
 * nor a group, so the Testing Terms are not listed under it. One enum rather
 * than two filters because that is what a row of pills is — exactly one on.
 * `all` includes retired, so the scopes cannot add up to more than it.
 */
export type AgreementScope = 'all' | 'org' | 'app' | 'group' | 'retired';

export type Agreement = {
  id: string;
  slug: string;
  name: string;
  appSlug: string | null;
  /** Owed only by this security group's members (auth#612). Never set with
   *  `appSlug`; both null is org-wide. See lib/agreement-audience.ts. */
  groupSlug: string | null;
  active: boolean;
  versionCount: number;
  /** `notice` is published on ridgewill.com and never owed. Fixed at creation. */
  kind: AgreementKind;
  /** The address on ridgewill.com/legal; null keeps it off the website. */
  publicSlug: string | null;
  publicSection: string | null;
  publicOrder: number;
  publicDescription: string;
  publicVersioned: boolean;
  inForce: { id: string; version: string; effectiveAt: string } | null;
  createdAt: string;
};

export type AgreementVersion = {
  id: string;
  version: string;
  contentFormat: string;
  contentHash: string;
  documentUrl: string | null;
  summary: string | null;
  effectiveAt: string;
  activatedAt: string | null;
  requiresReacceptance: boolean;
  acceptanceCount: number;
};

export type AgreementDetail = {
  id: string;
  slug: string;
  name: string;
  appSlug: string | null;
  groupSlug: string | null;
  active: boolean;
  createdAt: string;
  kind: AgreementKind;
  publicSlug: string | null;
  publicSection: string | null;
  publicOrder: number;
  publicDescription: string;
  publicVersioned: boolean;
  versions: AgreementVersion[];
  /** Unpublished versions, newest edit first. Listed without `content`; open
   *  one with `adminApi.agreementDraft`. See lib/agreement-drafts.ts. */
  drafts: AgreementDraftSummary[];
};

/**
 * A version that has not been published. Every field may be missing, because
 * a draft is somewhere to keep half a document. Never public, never owed and
 * never counted as a version; publishing one deletes it.
 */
export type AgreementDraftSummary = {
  id: string;
  version: string | null;
  summary: string | null;
  effectiveAt: string | null;
  requiresReacceptance: boolean;
  contentFormat: string;
  updatedAt: string;
  updatedByName: string | null;
};

export type AgreementDraft = AgreementDraftSummary & { content: string | null };

/** What a draft save sends: the whole form, with '' and null for blanks. */
export type DraftBody = {
  version?: string;
  content?: string;
  contentFormat?: 'markdown' | 'html';
  summary?: string;
  effectiveAt?: string | null;
  requiresReacceptance?: boolean;
};

export type PublishResult = { fannedOut: number; deferred: boolean };

export type Acceptance = {
  id: string;
  version: string;
  acceptedAt: string;
  withdrawnAt: string | null;
  archivedAt: string | null;
  ipAddress: string | null;
  userAgent: string | null;
  user: { id: string; pseudonymized: boolean; email: string | null; name: string | null };
};

export type Application = {
  id: string;
  slug: string;
  name: string;
  /**
   * Reachable by everybody, without a grant.
   *
   * `ALWAYS_ENTITLED_APPS` lives in the auth service's `lib/access.ts` and this
   * portal has no way to derive it, so the server says. Optional because an auth
   * that predates the flag sends nothing, and `undefined` reads as "an ordinary
   * application" — which shows one redundant row rather than hiding a real one.
   */
  alwaysEntitled?: boolean;
  description: string;
  platform: string;
  /**
   * Which devices a tester must have before they can send a tester card
   * (RidgeWill/platform#56). Null until somebody edits it, when the rule
   * follows the platforms (lib/tester-requirements.ts). Optional: an auth
   * from before it sends nothing.
   */
  testerRequirement?: Requirement | null;
  status: string;
  healthCheckUrl: string | null;
  /**
   * Where auth calls this app when one of its users closes their Ridgewill
   * identity, so the app can erase its own copy of that person's data
   * (RidgeWill/auth#225).
   *
   * Null is not neutral. Unlike `healthCheckUrl`, which is inert until somebody
   * runs a check, leaving this unset means the app is NEVER told about a
   * closure — and nothing goes wrong to say so. The data simply stays.
   */
  eventWebhookUrl: string | null;
  /**
   * Support ticket prefix, letters only: `KEY` makes `KEY-7HQ3MX` (Jon,
   * 2026-09-27). Null means the platform's `RW-`. Optional on the type because
   * an auth that predates the column omits it.
   */
  ticketPrefix?: string | null;
  /**
   * Offer this app in the customer support ticket forms: the account portal's
   * Support screen and the website (RidgeWill/auth#573). Its own setting, not
   * worked out from `status`. Optional on the type because an auth that
   * predates the column omits it.
   */
  supportListed?: boolean;
  /**
   * Offer this app on the tester card form at ridgewill.com/testers (Jon,
   * 2026-10-23). Its own switch; status does not decide. Optional on the type
   * because an auth that predates the column omits it.
   */
  testerListed?: boolean;
  /**
   * Does this app keep its OWN copy of the user?
   *
   * A different question from `eventWebhookUrl`, and the reason the "no closure
   * hook" badge is not simply "this field is null". Registering an application
   * says who may reach it; that is not the same as saying it stores anything.
   * The account and admin portals are reachable by everybody and hold nothing —
   * they are front ends over auth's own database — so a missing webhook on them
   * is correct rather than a gap (RidgeWill/auth#236).
   *
   * Defaults true server-side, so an app registered without mentioning custody
   * is treated as holding data and warns until somebody says otherwise.
   */
  holdsUserData: boolean;
  active: boolean;
  createdAt: string;
  /**
   * The SECURITY GROUPS that entitle this app, by slug.
   *
   * This was `roles` until RidgeWill/auth#146. Entitlement used to be a property
   * of a platform role, which meant granting an app to everybody required
   * minting a role to carry it; it is now assigned to groups and to individuals,
   * and `Basic Users` is the group every account joins on creation. Group
   * membership is edited from the person, not from here.
   */
  groups: string[];
};

/**
 * The global feed carries `subject`, because a feed without it is a list of
 * verbs with no nouns.
 *
 * `detail`, `userAgent` and `archivedAt` arrived with RidgeWill/auth#201, which
 * made this endpoint return the same event columns as the per-user one. Before
 * that, `EventDetail` had to fetch a page of the subject's activity to find the
 * row it had already been handed.
 *
 * This is now the ONLY event shape this app reads. `/admin/users/:id/activity`
 * still exists server-side and is no longer called from here: "one account's
 * events" is expressed as this feed with a `userId` filter, which keeps the
 * screen's other filters working. Two client methods for one question is how
 * they come to disagree.
 */
export type FeedEvent = {
  id: string;
  type: string;
  actorType: string;
  subject: { id: string; email: string | null; name: string | null; pseudonymized: boolean };
  actor: { id: string; email: string | null } | null;
  detail: unknown;
  ipAddress: string | null;
  userAgent: string | null;
  count: number;
  createdAt: string;
  archivedAt: string | null;
};

export type UserNote = {
  id: string;
  body: string;
  createdAt: string;
  updatedAt: string;
  /**
   * `role` is what the author bubble reads (RidgeWill/auth#285). A note from a
   * Support Admin is an observation; the same words from a Global Admin may be
   * a decision, and the note is read during an escalation where that differs.
   *
   * Optional for the same reason `AdminUser.image` is — an older auth omits it,
   * and the bubble simply says less.
   */
  author: { id: string; email: string | null; name: string | null; role?: string | null } | null;
};

/**
 * Where one account stands on ONE agreement it is subject to.
 *
 * ── Three states, not two ────────────────────────────────────────────
 *
 * `accepted` false covers both "has not got round to it" and "agreed and then
 * withdrew", and those are different conversations. `withdrawnAt` is what
 * separates them, and it is set only when they accepted THIS version and later
 * took it back.
 */
export type UserAgreement = {
  agreementSlug: string;
  name: string;
  /** Both null means org-wide. Blank would read as missing data on the field
   *  that decides who owes it. */
  appSlug: string | null;
  groupSlug: string | null;
  version: string;
  effectiveAt: string;
  requiresReacceptance: boolean;
  accepted: boolean;
  acceptedAt: string | null;
  withdrawnAt: string | null;
  /**
   * Both halves, for a member under 18 (auth#615): their own acceptance and a
   * guardian's. Null for everyone else, and absent from an older auth.
   */
  halves?: {
    self: boolean;
    selfAcceptedAt?: string | null;
    guardian: boolean;
    guardianAcceptedAt: string | null;
    guardianId: string | null;
  } | null;
};

export type OauthClient = {
  id: string;
  clientId: string;
  name: string | null;
  type: string | null;
  public: boolean | null;
  disabled: boolean | null;
  redirectUris: string[];
  createdAt: string | null;
  trusted: boolean;
};

export type ApiKey = {
  id: string;
  name: string | null;
  prefix: string | null;
  start: string | null;
  enabled: boolean | null;
  ownerId: string;
  owner: { id: string; email: string | null } | null;
  metadata: Record<string, unknown> | null;
  /**
   * What the key may do, in the vocabulary it was issued in. OPTIONAL because an
   * auth service older than the grants editor does not send it, and a missing
   * list must read as "unknown" rather than as "holds nothing".
   */
  grants?: CredentialGrant[];
  /** The key holds permissions no grant describes. Editing is refused for it,
   *  since saving from grants would drop them. */
  unmanagedPermissions?: boolean;
  /** When the secret this key had before its last rotation stops working, while
   *  it still works (RidgeWill/auth#649). Absent from an older auth service. */
  previousSecretValidUntil?: string | null;
  requestCount: number | null;
  lastRequest: string | null;
  expiresAt: string | null;
  createdAt: string;
};

/** One credential, for its own screen (RidgeWill/auth#650). */
export type ApiKeyDetail = ApiKey & {
  updatedAt?: string;
  /** Each previous secret still working after a rotation, newest first. Only
   *  open windows are returned, so a row here is a secret that works now. */
  previousSecrets: { start: string | null; replacedAt: string; validUntil: string }[];
};

export type Credentials = {
  oauthClients: OauthClient[];
  apiKeys: { total: number; limit: number; offset: number; keys: ApiKey[] };
};

export type UserSession = {
  id: string;
  createdAt: string;
  updatedAt: string;
  expiresAt: string;
  ipAddress: string | null;
  userAgent: string | null;
  impersonatedBy?: string | null;
};

/**
 * One passkey on somebody else's account, as staff see it.
 *
 * Read only. There is no removal here and that is a decision, not a gap — see
 * the panel in screens/UserDetail.tsx.
 *
 * `publicKey` and `credentialID` are not in this type because the server does
 * not send them. Neither answers a support question.
 */
export type Passkey = {
  id: string;
  /** What the OWNER typed. A label they chose, not a verified fact. */
  name: string | null;
  createdAt: string | null;
  /**
   * NULL means "not seen since 2026-08-25", when auth#154 added the column —
   * NOT "never used". A passkey in daily use reads as null until its next
   * authentication, so nothing may render this as never.
   */
  lastUsedAt: string | null;
  /** Synced to a keychain, so it survives losing the device it was made on. */
  backedUp: boolean;
  /**
   * The provider the authenticator identified itself as — "1Password",
   * "Windows Hello" — resolved by auth from the AAGUID.
   *
   * Null far more often than it is set, and normally so: Apple zeroes the
   * AAGUID under the default attestation flow, so iCloud Keychain and Apple
   * Passwords resolve to nothing. Null means UNIDENTIFIED, never "no
   * authenticator".
   */
  authenticator: string | null;
  /**
   * The raw model identifier, carried even when the name lookup failed —
   * especially then, since it is what makes an unmapped authenticator
   * reportable. All-zero means the platform anonymised it; null means the
   * passkey predates the column.
   */
  aaguid: string | null;
};

export type HealthResult = {
  id: string;
  slug: string;
  name: string;
  url: string | null;
  state: 'ok' | 'failing' | 'refused' | 'unreachable';
  status?: number;
  latencyMs?: number;
  code?: string;
  reason?: string;
};

export type HealthReport = { checkedAt: string; configured: number; results: HealthResult[] };

/**
 * What the last health probe found, per monitored application (org-admin#57).
 *
 * A READ, not a probe. `checkHealth()` below is the probe and stays a POST for
 * the reasons stated there; this reports the row that one already wrote, so the
 * Uptime section can load on mount without every visit becoming live outbound
 * traffic to every registered app.
 */
export type UptimeReport = {
  /** When the most recent probe ran, or null if none ever has. */
  lastCheckedAt: string | null;
  /**
   * Registered and active but with NO health check URL.
   *
   * A count rather than an omission, because "everything is healthy" and "we
   * are watching two of nine applications" look identical on a screen that only
   * lists what it monitors.
   */
  unmonitored: number;
  services: {
    id: string;
    slug: string;
    name: string;
    url: string | null;
    /**
     * NULL means this application was not in the last run — never "healthy".
     *
     * It is what an app registered since the last check looks like, and what an
     * app looks like before anybody has pressed the button. The server
     * deliberately does not carry a state forward from an older run: that would
     * report an app as up on the strength of a check that no longer describes
     * it.
     */
    state: HealthResult['state'] | null;
  }[];
};

/**
 * How big the activity log is, and how long clearing it takes (org-admin#16).
 *
 * The panel exists to be the measured trigger for RidgeWill/auth#51 — partition
 * `activity_events` by month once it passes a few million rows, or once the
 * purge takes more than a few seconds. A deferred decision with no trigger
 * becomes a permanent one by default, and this is what stops that happening
 * quietly.
 */
export type ActivityStorage = {
  table: {
    /**
     * NULL means the server has no figure, NOT an empty table.
     *
     * The count is exact while the table is small and a planner estimate once it
     * is not; `pg_class.reltuples` is -1 until ANALYZE has run, and there is no
     * third source. Rendering that as zero would be the panel confidently
     * reporting an empty log, which is the one wrong answer nobody would
     * question.
     */
    rows: number | null;
    /** Which of the two `rows` is. The screen must say so: presenting a planner
     *  estimate as a fact is how a number gets quoted in a decision. */
    rowsExact: boolean;
    /** Heap plus indexes plus TOAST, which is what the table costs. */
    totalBytes: number;
    heapBytes: number;
    indexBytes: number;
    oldestAt: string | null;
  };
  /** What the purge is enforcing, read from the same functions it reads. */
  retention: { routineDays: number; privilegedYears: number };
  purge: {
    /**
     * The recorded runs, newest first, and possibly EMPTY.
     *
     * Empty means no run has been recorded since the auth service started
     * writing them down, not that the purge is not running. The screen says
     * which, because "no history yet" and "the job has stopped" look identical
     * here and only one of them needs somebody.
     */
    runs: {
      at: string;
      ms: number;
      ok: boolean;
      /** Rows each half deleted, or null when the job failed before reporting.
       *  What separates a slow purge that was working from one that was not. */
      routine: number | null;
      privileged: number | null;
    }[];
  };
  /** auth#51's thresholds as the server reads them, plus whether either is met.
   *  Evaluated server-side so an alert added later cannot disagree with this
   *  screen about what "a few" means. */
  trigger: { rows: number; ms: number; rowsMet: boolean; msMet: boolean };
};

type Page<K extends string, T> = { total: number; limit: number; offset: number } & Record<K, T[]>;

/**
 * A paged response that also reports what it SEARCHED on.
 *
 * `terms` is the server's answer, not an echo of the request: blank chips are
 * dropped and the count is capped, so the two can differ. The chip bar renders
 * a warning when they do, rather than leaving the admin to notice that results
 * are wider than the chips on screen claim.
 */
type Searched<K extends string, T> = Page<K, T> & { terms: string[] } & Record<K, T[]>;

/** One branding value, or null meaning "corporate". `panelWatermark` is the
 *  one boolean: Show or Hide, with null meaning automatic. */
export type BrandingValues = Record<string, string | boolean | null>;

/** What is wrong with a proposed write, as the server describes it. */
export interface BrandingProblem {
  field: string;
  message: string;
  contrast?: { measured: number | null; required: number; against: string };
}

export interface BrandingResponse {
  client: { clientId: string; name: string | null };
  /** The row as stored. */
  stored: BrandingValues;
  /** What the login page would ACTUALLY render from it — which can differ from
   *  `stored` when a row predates a rule change. Showing only the row would
   *  tell an admin their branding is set while the page ignores it. */
  effective: BrandingValues;
  /** The tokens a client may set, from the server, so this app does not carry a
   *  copy of the list that goes stale on the next widening. */
  brandable: string[];
}

/** One network with a sign-in ban in force, as Support -> Suspended IPs lists it. */
export interface SuspendedNetwork {
  /** The key the detail view takes: `ip:<addr>`. */
  network: string;
  /** As staff would type it: the IPv4 address, or the IPv6 /64. */
  address: string;
  location: string | null;
  /** A staff ban bars every account on it. */
  wholeNetwork: boolean;
  /** Accounts whose owner barred it. */
  accounts: number;
  failedAttempts: number;
  blockedAttempts: number;
  lastAttemptAt: string | null;
  until: string;
  createdAt: string;
}

export interface SignInBan {
  key: string;
  scope: 'network' | 'account';
  source: 'owner' | 'staff' | string;
  account: { id: string; name: string | null; email: string | null } | null;
  bannedBy: { id: string; name: string | null; email: string | null } | null;
  location: string | null;
  failedAttempts: number;
  blockedAttempts: number;
  lastAttemptAt: string | null;
  until: string;
  createdAt: string;
}

export const adminApi = {
  /**
   * What this session may do, as `resource:action` strings.
   *
   * Derived on the server from the same statement its endpoints enforce with,
   * so the portal can disable a control it would be refused without holding a copy
   * of the role rules. See lib/capabilities.tsx for the contract — the short
   * version is that it disables and never promises.
   */
  /**
   * Per-app login branding (RidgeWill/org-admin#86).
   *
   * Addressed by SLUG, not by application id: `oauthClient.clientId` is the app
   * slug, and the branding row hangs off that. The portal reaches these from
   * the Applications detail screen, which has the slug in hand.
   */
  clientBranding: (slug: string) =>
    api.get<BrandingResponse>(`/admin/client-branding/${encodeURIComponent(slug)}`),
  setClientBranding: (slug: string, body: BrandingValues) =>
    api.put<{ stored: BrandingValues; effective: BrandingValues }>(
      `/admin/client-branding/${encodeURIComponent(slug)}`,
      body,
    ),
  /** Ask what WOULD happen, without storing it — the preview's source of truth.
   *  The contrast rules live on the server; a second copy here would drift, and
   *  the drift would be a form that accepts what the page then ignores. */
  checkClientBranding: (slug: string, body: BrandingValues) =>
    api.post<{ problems: BrandingProblem[]; effective: BrandingValues }>(
      `/admin/client-branding/${encodeURIComponent(slug)}/check`,
      body,
    ),

  capabilities: () => api.get<{ capabilities: string[] }>('/admin/capabilities'),
  /** Every tester card (RidgeWill/platform#56). `tester:read`. */
  testers: () =>
    api.get<{ testers: TesterCardRow[]; apps: { slug: string; name: string; status: string }[] }>('/admin/testers'),
  /**
   * Add testers to a group from the Testers screen. Unlike the group's own
   * members endpoint, it also marks the group as holding testers, so a person
   * who deletes their card leaves it.
   */
  addTestersToGroup: (slug: string, userIds: string[]) =>
    api.post<{ slug: string; added: string[]; alreadyMembers: string[] }>('/admin/testers/group-members', {
      slug,
      userIds,
    }),

  overview: () => api.get<Overview>('/admin/overview'),

  /** GET, and cheap by construction: every figure behind it is a catalog read
   *  or a row the retention job already wrote. See the endpoint's own comment in
   *  the auth service's plugins/admin-console.ts for why that matters here. */
  activityStorage: () => api.get<ActivityStorage>('/admin/system-health/activity'),

  /** The last recorded probe result per application. A GET because it reads
   *  rather than probes — see `checkHealth` for the one that reaches out. */
  uptime: () => api.get<UptimeReport>('/admin/system-health/uptime'),

  // ── Users ──────────────────────────────────────────────────────────
  /**
   * OURS, not Better Auth's `/admin/list-users`.
   *
   * The plugin's endpoint pushes a single `where` entry from one `searchValue`
   * against one `searchField`, so it can express neither "alice AND smith" nor
   * "match an email or a name" — and the chip search is both, on every list.
   *
   * The MUTATIONS below still go to the plugin, deliberately. They carry its
   * permission model and its hooks, and reimplementing one to get a query shape
   * is how two code paths come to disagree about what banning means.
   */
  users: (
    opts: {
      limit?: number;
      offset?: number;
      terms?: string[];
      /**
       * Whether automated agents are included (RidgeWill/platform#31). Omitted
       * means BOTH, which is what the id lookup below depends on: a default
       * that hid agents would make an agent's record unreachable by its id and
       * read as "no such account".
       */
      agents?: 'exclude' | 'only';
    } = {},
  ) =>
    api.get<Searched<'users', AdminUser>>(
      `/admin/users${q({
        limit: opts.limit ?? 50,
        offset: opts.offset,
        q: opts.terms,
        agents: opts.agents,
      })}`,
    ),
  /**
   * ONE account, by id — over the search endpoint, because there is no
   * `/admin/users/:id`.
   *
   * ── Why this is not a workaround to be replaced ─────────────────────
   *
   * `/admin/users` searches `email`, `name` AND `id`, deliberately: the auth
   * service's own comment says the thing an admin pastes into that box during an
   * incident is frequently an id out of a log line. So an id lookup is a
   * first-class use of the endpoint rather than a query shape being abused.
   *
   * ── The exact match is the part that matters ────────────────────────
   *
   * The search is `contains`, so it can return more than one row — an id that
   * happens to be a substring of another, or of somebody's name. Taking the
   * first would open the wrong account, silently, on a screen whose entire job
   * is telling you which account you are looking at. So the id is matched
   * exactly here and anything else is a miss.
   *
   * `limit: 5` rather than 1 for the same reason: with a limit of one, a single
   * unrelated row sorted ahead of the real account would make a valid id look
   * like a missing one.
   *
   * The 404 is shaped like the server's own so `describe()` renders it the same
   * way, but the message is ours — a bare "Not found" here would be read as the
   * caller lacking the admin role, which is what that status usually means on
   * this API and is not what happened.
   */
  user: async (id: string): Promise<AdminUser> => {
    const found = await api.get<Searched<'users', AdminUser>>(
      `/admin/users${q({ limit: 5, q: [id] })}`,
    );
    const exact = found.users.find((u) => u.id === id);
    if (!exact) throw new ApiError(404, `No account has the id ${id}.`, CLIENT_ERROR);
    return exact;
  },
  /**
   * ROLE IS AN ARRAY ON THE WIRE, even though the column is one comma-separated
   * string. Those are different shapes and conflating them was #28.
   *
   * `/admin/set-role` validates BEFORE it splits:
   *
   *   const inputRoles = Array.isArray(ctx.body.role) ? ctx.body.role : [ctx.body.role];
   *   for (const role of inputRoles) if (!roles[role]) throw APIError.from('BAD_REQUEST', …)
   *   // …only now: updateUser(id, { role: parseRoles(ctx.body.role) })
   *
   * So a joined `"user,keyodo-user"` is looked up whole, `roles[…]` is
   * undefined, and every multi-role save is refused. The comma handling the
   * caller was reasoning about is real, and it lives on the far side of the
   * check it never reached. The body schema is
   * `z.union([z.string(), z.array(z.string())])` — the array is the shape that
   * survives validation.
   *
   * Typed `string | string[]` rather than `string[]` because the single-role
   * string is still valid and still used; narrowing it would be a change to
   * callers this bug gives no reason to make.
   */
  setRole: (userId: string, role: string | string[]) => api.post('/admin/set-role', { userId, role }),
  banUser: (userId: string, banReason?: string, banExpiresIn?: number) =>
    api.post('/admin/ban-user', { userId, banReason, banExpiresIn }),
  unbanUser: (userId: string) => api.post('/admin/unban-user', { userId }),
  /**
   * Sign-in bans (auth#587): Support -> Suspended IPs. Reading needs
   * `support:read`; `canBan` on each read says whether this session may ban
   * and lift (`user:ban`, a Support Admin and up).
   */
  signInBans: (opts: { limit?: number; offset?: number } = {}) =>
    api.get<{ total: number; canBan: boolean; networks: SuspendedNetwork[] }>(
      `/admin/signin-bans?limit=${opts.limit ?? 50}&offset=${opts.offset ?? 0}`,
    ),
  signInBanNetwork: (network: string) =>
    api.get<{ network: string; address: string; canBan: boolean; bans: SignInBan[] }>(
      `/admin/signin-bans/network?network=${encodeURIComponent(network)}`,
    ),
  banSignInNetwork: (ip: string, until: string) =>
    api.post<{ key: string; network: string; until: string }>('/admin/signin-bans', { ip, until }),
  liftSignInBan: (key: string) => api.post<{ lifted: true }>('/admin/signin-bans/lift', { key }),
  /**
   * Create an account for somebody and email them their way in (org-admin#83).
   *
   * REPLACES `createUser`, which took a password the administrator typed and
   * therefore knew. Here the service generates one, emails it, and the response
   * deliberately does not contain it — putting it back on this screen would
   * leave the feature where it started with an extra step.
   *
   * `rootApi`, because the route is an Express handler on the auth service
   * rather than a Better Auth plugin endpoint, so it is not under `/api/auth`.
   *
   * `delivered` is whether the mail was accepted, and it is worth showing: an
   * account exists either way, and "created but we could not email them" is the
   * one outcome where the admin has to do something next.
   */
  inviteUser: (body: { email: string; name: string }) =>
    rootApi.post<{ id: string; email: string; delivered: boolean }>('/admin/invite-user', body),
  /** Sessions, so an incident can end somebody's access without ending their
   *  account. `session.revoked` is already a user-visible activity type. */
  userSessions: (userId: string) =>
    api.post<{ sessions: UserSession[] }>('/admin/list-user-sessions', { userId }),
  revokeUserSessions: (userId: string) => api.post('/admin/revoke-user-sessions', { userId }),

  passkeys: (userId: string) =>
    api.get<{ total: number; passkeys: Passkey[] }>(`/admin/users/${userId}/passkeys`),
  /**
   * `sort` is applied by the SERVER (RidgeWill/auth#286), not in the browser.
   *
   * The endpoint returns the newest notes up to its page limit, so sorting the
   * fetched rows would order the page rather than the list: "oldest first" would
   * show the oldest of the newest fifty, which is wrong and looks right. The
   * same reason the Users search is server-side.
   */
  notes: (userId: string, opts: { sort?: 'newest' | 'oldest' } = {}) =>
    api.get<Page<'notes', UserNote> & { sort: 'newest' | 'oldest' }>(
      `/admin/users/${encodeURIComponent(userId)}/notes${q({ sort: opts.sort })}`,
    ),
  /**
   * Every agreement this account is subject to, and where it stands on each.
   *
   * ── `agreement: ['read']`, which an Auth Admin does not hold ────────
   *
   * Deliberate on the server: publishing terms of service is Legal's, and the
   * role that administers accounts holds no `agreement` permission. So this call
   * 404s for a perfectly valid Auth Admin session while every other panel on the
   * same screen works — which is why the panel renders that as a sentence rather
   * than as a failure.
   *
   * `pendingCache` is the cache a consuming app actually gates on, returned
   * alongside the record rather than instead of it. The two disagreeing is not
   * noise: a slug here that the record says is signed is somebody being asked to
   * re-accept something they already have, with no other symptom.
   */
  userAgreements: (userId: string) =>
    api.get<{ agreements: UserAgreement[]; pendingCache: string[] }>(
      `/admin/users/${encodeURIComponent(userId)}/agreements`,
    ),
  /** Read-only: the account's Family Groups, members, attestation and history (org-admin#187). */
  userFamily: (userId: string) => api.get<UserFamily>(`/admin/users/${encodeURIComponent(userId)}/family`),
  /** The member's date of birth and its change history (auth#585, auth#672). Needs `birthDate:read`. */
  userBirthDate: (userId: string) =>
    api.get<UserBirthDate>(`/admin/users/${encodeURIComponent(userId)}/birth-date`),
  /**
   * Support's correction (auth#672). `ticketReference` is free text stored with
   * the change and never looked up against support: it must not tie the two
   * records together, so a ticket can still be deleted.
   */
  correctBirthDate: (userId: string, body: { dateOfBirth: string; ticketReference?: string }) =>
    api.post<{ ageBand: AgeBand; unfrozen: boolean }>(
      `/admin/users/${encodeURIComponent(userId)}/birth-date`,
      body,
    ),
  addNote: (userId: string, body: string) => api.post<UserNote>(`/admin/users/${userId}/notes`, { body }),
  deleteNote: (noteId: string) => api.del(`/admin/notes/${noteId}`),

  // ── Activity ───────────────────────────────────────────────────────
  activityFeed: (
    opts: {
      limit?: number;
      offset?: number;
      type?: string;
      since?: string;
      /** The other end of the range. `since` alone can only say "recently", and
       *  the question asked during an investigation has two dates in it. */
      until?: string;
      sort?: 'newest' | 'oldest';
      userId?: string;
      terms?: string[];
    } = {},
  ) =>
    api.get<Searched<'events', FeedEvent> & { sort: 'newest' | 'oldest' }>(
      `/admin/activity${q({
        limit: opts.limit ?? 50,
        offset: opts.offset,
        type: opts.type,
        since: opts.since,
        until: opts.until,
        sort: opts.sort,
        userId: opts.userId,
        q: opts.terms,
      })}`,
    ),

  /**
   * ONE event, in full (RidgeWill/auth#284).
   *
   * A REAL ENDPOINT, unlike `user()` and `application()` above, which pick their
   * record out of a listing. The activity feed has no id filter and cannot get
   * one cheaply — searching `activity_events` by id would mean a term filter
   * over the largest table on the platform — and the event drill-in has to open
   * from a pasted link, which is what a screen means.
   *
   * Returns exactly what a row of `activityFeed` returns. The auth service holds
   * that to a single shared projection rather than a second copy, because the
   * two drifting apart is auth#201: the feed omitted `detail`, `userAgent` and
   * `archivedAt`, nothing failed, and this portal rendered "records no
   * structured detail" for events with plenty.
   *
   * A 404 here is ordinary. Events are purged past their retention window, so an
   * id out of an old incident note stops resolving — which is a different thing
   * from a bad link, and the screen says so.
   */
  activityEvent: (id: string) =>
    api.get<FeedEvent>(`/admin/activity/${encodeURIComponent(id)}`),

  // ── Agreements ─────────────────────────────────────────────────────
  /** Paged since auth#55 — it returned every row, and 1,148 of them made the
   *  screen unresponsive. `scope` is the filter pills. */
  agreements: (
    opts: {
      limit?: number;
      offset?: number;
      scope?: AgreementScope;
      terms?: string[];
    } = {},
  ) =>
    api.get<Searched<'agreements', Agreement> & { scope: AgreementScope }>(
      `/admin/agreements${q({
        limit: opts.limit ?? 50,
        offset: opts.offset,
        scope: opts.scope,
        q: opts.terms,
      })}`,
    ),
  agreement: (id: string) => api.get<AgreementDetail>(`/admin/agreements/${id}`),
  /** `appSlug` OR `groupSlug`, never both (auth refuses SCOPE_CONFLICT); neither
   *  is org-wide. The audience cannot be changed afterwards. */
  createAgreement: (body: {
    slug: string;
    name: string;
    appSlug?: string | null;
    groupSlug?: string | null;
    /** Default `agreement`. Fixed afterwards, like the audience. */
    kind?: AgreementKind;
  }) =>
    api.post<{ id: string; slug: string; name: string; appSlug: string | null; groupSlug: string | null }>(
      '/admin/agreements',
      body,
    ),
  setAgreementActive: (id: string, active: boolean) =>
    api.patch<{ id: string; active: boolean; cachesChanged: number }>(`/admin/agreements/${id}`, { active }),
  /** The "On ridgewill.com" panel. Only the fields that changed; auth refuses
   *  a taken address with PUBLIC_SLUG_TAKEN. */
  setAgreementPublic: (id: string, body: PublicPatch) =>
    api.patch<Partial<PublicFields> & { id: string }>(`/admin/agreements/${id}`, body),
  publishVersion: (
    id: string,
    body: {
      version: string;
      content: string;
      contentFormat?: 'markdown' | 'html';
      summary?: string;
      effectiveAt: string;
      requiresReacceptance?: boolean;
    },
  ) => api.post<PublishResult>(`/admin/agreements/${id}/versions`, body),
  agreementDraft: (id: string, draftId: string) =>
    api.get<AgreementDraft>(`/admin/agreements/${id}/drafts/${encodeURIComponent(draftId)}`),
  createDraft: (id: string, body: DraftBody) =>
    api.post<AgreementDraft>(`/admin/agreements/${id}/drafts`, body),
  updateDraft: (id: string, draftId: string, body: DraftBody) =>
    api.patch<AgreementDraft>(`/admin/agreements/${id}/drafts/${encodeURIComponent(draftId)}`, body),
  deleteDraft: (id: string, draftId: string) =>
    api.del<unknown>(`/admin/agreements/${id}/drafts/${encodeURIComponent(draftId)}`),
  /** Publishes exactly as `publishVersion` does and deletes the draft. 400
   *  DRAFT_INCOMPLETE when version, content or effectiveAt is missing; 409
   *  VERSION_EXISTS when the version string is taken. */
  publishDraft: (id: string, draftId: string, body: { effectiveAt?: string } = {}) =>
    api.post<PublishResult>(`/admin/agreements/${id}/drafts/${encodeURIComponent(draftId)}/publish`, body),
  acceptances: (id: string, limit = 50) =>
    api.get<Page<'acceptances', Acceptance>>(`/admin/agreements/${id}/acceptances${q({ limit })}`),

  // ── Applications ───────────────────────────────────────────────────
  applications: (opts: { status?: string; terms?: string[] } = {}) =>
    api.get<{ applications: Application[]; terms: string[]; status: string | null }>(
      `/admin/applications${q({ status: opts.status, q: opts.terms })}`,
    ),
  /**
   * ONE application, by SLUG or by id — over the unfiltered listing.
   *
   * ── THE SLUG HALF IS NOT OPTIONAL, AND WAS MISSED ONCE ─────────────
   *
   * The portal links applications by slug (org-admin#89), because the slug is
   * immutable and an id means nothing in a pasted link. This function is what
   * turns that URL segment back into a record, and it matched `a.id` alone — so
   * every application row 404'd with "No application has the id keyodo" the
   * moment the links changed.
   *
   * Worth naming the mistake precisely rather than just fixing it:
   * RidgeWill/auth#446 taught the SERVER to resolve either key, and this client
   * never calls that endpoint. Changing one end of a path without reading the
   * other is what broke it, and no test caught it because both ends were
   * individually correct.
   *
   * ── Over the listing, not the detail endpoint ──────────────────────
   *
   * This comment used to say there is no `/admin/applications/:id`. There is.
   * The listing is still what this uses, because the detail screen renders from
   * list fields and switching would be a larger change than the reason for it.
   *
   * Cheaper than it looks, and bounded in a way the users listing is not: the
   * registry is the set of applications this platform has, which is single
   * digits and grows when somebody ships a product. Paging it has never been
   * necessary and the endpoint does not offer it.
   *
   * NO FILTERS, deliberately. The detail screen must open for a retired
   * application and for one whose status the reader was not looking at, so this
   * asks for everything and picks. A `status` here would make a link work or not
   * depending on what the app happens to be, which is the failure the record
   * screens exist to remove.
   */
  application: async (key: string): Promise<Application> => {
    const { applications } = await adminApi.applications();
    // Slug first, because it is the form every link now carries. Both, because
    // id links already in circulation must keep working.
    const exact = applications.find((a) => a.slug === key) ?? applications.find((a) => a.id === key);
    if (!exact) throw new ApiError(404, `No application has the slug or id ${key}.`, CLIENT_ERROR);
    return exact;
  },
  /**
   * POST, and now also on a timer (org-admin#57).
   *
   * ── The rule this relaxed, and the part that did not ────────────────
   *
   * This was "never on mount", on the grounds that a check firing when a screen
   * opens is a scanner with a refresh button. Jon's call, 2026-08-27: the Uptime
   * section checks on arrival and then on an interval the admin picks, because a
   * status page whose statuses are from whenever somebody last pressed a button
   * is not a status page.
   *
   * It stays a POST, and the reason narrowed rather than disappeared. These runs
   * are asked for, at a rate somebody set. A GET would additionally be fired by
   * things nobody chose — a prefetcher, a reload, a link preview — and each one
   * is real outbound traffic from inside the private network.
   *
   * `auto` tells the server which kind of run it was. It does not change what is
   * probed; it throttles how often the run is written to the audit log, because
   * a ten-second timer left open overnight would otherwise put thousands of rows
   * a day into it. See the endpoint in the auth service's plugins/applications.ts.
   *
   * The server refuses private address space, pins the vetted address and does
   * not follow redirects — see the auth service's lib/url-guard.ts.
   */
  checkHealth: (opts: { auto?: boolean } = {}) =>
    api.post<HealthReport>('/admin/applications/health', opts.auto ? { auto: true } : {}),
  createApplication: (body: {
    slug: string;
    name: string;
    description?: string;
    platform?: string;
    status?: string;
    healthCheckUrl?: string | null;
    eventWebhookUrl?: string | null;
    ticketPrefix?: string | null;
    holdsUserData?: boolean;
    groups?: string[];
  }) => api.post<Application>('/admin/applications', body),
  /**
   * No `slug`. The server rejects one with a 400 rather than ignoring it,
   * because a rename would orphan every agreement scoped to the app and
   * invalidate audiences already minted — silently, with nothing failing.
   */
  updateApplication: (
    id: string,
    body: Partial<
      Pick<
        Application,
        | 'name'
        | 'description'
        | 'platform'
        | 'testerRequirement'
        | 'status'
        | 'healthCheckUrl'
        | 'eventWebhookUrl'
        | 'ticketPrefix'
        | 'supportListed'
        | 'testerListed'
        | 'holdsUserData'
        | 'active'
      >
    >,
  ) =>
    api.patch<
      Application & {
        retired: boolean;
        affectedAgreements: number;
        /** Closures the absence of a webhook had parked, released by setting one. */
        requeuedNotices: number;
        /** Closures dropped because the app was declared to hold no data. */
        discardedNotices: number;
      }
    >(`/admin/applications/${id}`, body),
  /**
   * Which platform events this application has asked for (RidgeWill/auth#416).
   *
   * `available` comes from the SERVICE rather than a list in this front end,
   * deliberately: a new event type otherwise ships and nobody can subscribe to
   * it, because the portal does not know it exists. Render the menu from what
   * comes back.
   *
   * `deliverable` says whether an event would have anywhere to go — it is false
   * when no `eventWebhookUrl` is registered. Subscribing without one is allowed
   * and reported rather than refused, so that the two settings can be made in
   * either order.
   */
  applicationEventSubscriptions: (id: string) =>
    api.get<{
      id: string;
      eventTypes: string[];
      available: string[];
      deliverable: boolean;
    }>(`/admin/applications/${id}/event-subscriptions`),
  /**
   * The COMPLETE set, not a delta. An omitted type is unsubscribed.
   *
   * `identity.closed` is not in `available` and cannot be sent: it goes to every
   * app holding that user's data, and an app that could unsubscribe from an
   * erasure notice is an app that can quietly stop honouring erasures.
   */
  setApplicationEventSubscriptions: (id: string, eventTypes: string[]) =>
    api.put<{ id: string; eventTypes: string[]; deliverable: boolean }>(
      `/admin/applications/${id}/event-subscriptions`,
      { eventTypes },
    ),
  /** The COMPLETE set of groups, not a delta. An empty array revokes all. */
  setApplicationGrants: (id: string, groups: string[]) =>
    api.put<{ id: string; groups: string[] }>(`/admin/applications/${id}/grants`, { groups }),
  /** The paid tiers an application sells, lowest first (auth#567). */
  applicationTiers: (id: string) =>
    api.get<{ id: string; tiers: ApplicationTier[] }>(`/admin/applications/${encodeURIComponent(id)}/tiers`),
  /** The COMPLETE list, lowest first. Refused if it removes or renames a tier somebody holds. */
  setApplicationTiers: (id: string, tiers: ApplicationTierInput[]) =>
    api.put<{ id: string; tiers: ApplicationTier[] }>(`/admin/applications/${encodeURIComponent(id)}/tiers`, { tiers }),
  /** Every release note, drafts included, newest version first (keyodo#709). */
  releaseNotes: (id: string) =>
    api.get<{ id: string; notes: ReleaseNote[]; versions: AppVersions }>(
      `/admin/applications/${encodeURIComponent(id)}/release-notes`,
    ),
  /** A new draft. 409 VERSION_EXISTS when the version already has a note. */
  createReleaseNote: (id: string, body: ReleaseNoteInput) =>
    api.post<{ note: ReleaseNote }>(`/admin/applications/${encodeURIComponent(id)}/release-notes`, body),
  /** Replace a note's fields. Works on a published note too, to fix it in place. */
  updateReleaseNote: (id: string, noteId: string, body: ReleaseNoteInput) =>
    api.put<{ note: ReleaseNote }>(
      `/admin/applications/${encodeURIComponent(id)}/release-notes/${encodeURIComponent(noteId)}`,
      body,
    ),
  /**
   * Make a draft public. 400 RELEASE_NOTE_INCOMPLETE says what is missing.
   * `localToday` dates an undated draft with the publisher's own day, rather
   * than the server's UTC one (RidgeWill/auth#642).
   */
  publishReleaseNote: (id: string, noteId: string, localToday: string) =>
    api.post<{ note: ReleaseNote }>(
      `/admin/applications/${encodeURIComponent(id)}/release-notes/${encodeURIComponent(noteId)}/publish`,
      { localToday },
    ),
  /** Drafts only. A published note is refused with 409. */
  deleteReleaseNote: (id: string, noteId: string) =>
    api.del<{ id: string }>(`/admin/applications/${encodeURIComponent(id)}/release-notes/${encodeURIComponent(noteId)}`),
  /** A published note back to a draft. People only (RidgeWill/auth#680). */
  unpublishReleaseNote: (id: string, noteId: string) =>
    api.post<{ note: ReleaseNote; versions: AppVersions }>(
      `/admin/applications/${encodeURIComponent(id)}/release-notes/${encodeURIComponent(noteId)}/unpublish`,
      {},
    ),
  /** Mark a release Required, or not. A published required release is the minimum version. */
  setReleaseRequired: (id: string, noteId: string, required: boolean) =>
    api.put<{ note: ReleaseNote; versions: AppVersions }>(
      `/admin/applications/${encodeURIComponent(id)}/release-notes/${encodeURIComponent(noteId)}/required`,
      { required },
    ),
  /** Set the latest and minimum versions by hand, until the next release change recalculates them. */
  setAppVersions: (id: string, body: { latestVersion: string | null; minimumVersion: string | null }) =>
    api.put<{ versions: AppVersions }>(`/admin/applications/${encodeURIComponent(id)}/versions`, body),
  /** Send the current versions to the app again, and wait for its answer. */
  syncAppVersions: (id: string) =>
    api.post<{ versions: AppVersions }>(`/admin/applications/${encodeURIComponent(id)}/versions/sync`, {}),
  /**
   * Every role, and the subset a GROUP may carry.
   *
   * The two differ by exactly one name — `global-admin` is the way back in, and
   * a break-glass role reachable by editing a membership list is not
   * break-glass. `groupAssignable` comes from the server rather than being
   * `roles` minus a hardcoded string here, because a copy of that rule is a copy
   * that stops agreeing the day the role is renamed (RidgeWill/auth#146).
   */
  roles: () =>
    api.get<{ roles: string[]; groupAssignable?: string[]; breakGlass?: string; labels?: RoleLabels }>(
      '/admin/roles',
    ),

  // ── Security groups ────────────────────────────────────────────────
  securityGroups: () => api.get<{ groups: SecurityGroup[] }>('/admin/security-groups'),
  securityGroup: (slug: string) =>
    api.get<SecurityGroupDetail>(`/admin/security-groups/${encodeURIComponent(slug)}`),
  securityGroupMembers: (slug: string, opts: { limit?: number; offset?: number; term?: string } = {}) =>
    api.get<{ total: number; limit: number; offset: number; terms: string[]; members: GroupMember[] }>(
      `/admin/security-groups/${encodeURIComponent(slug)}/members${q({
        limit: opts.limit,
        offset: opts.offset,
        q: opts.term,
      })}`,
    ),
  /**
   * Take one account out of a group.
   *
   * `removed: false` is not a failure — it means they were not a member, which
   * is what a double-submitted request looks like. The caller should say so
   * rather than reporting a removal that did not happen.
   */
  /** What one account can reach, and where each piece of it came from. */
  userAccess: (id: string) => api.get<UserAccess>(`/admin/users/${encodeURIComponent(id)}/access`),

  /** A person's subscriptions, the grant controls, and their history (auth#540). */
  userSubscriptions: (id: string) =>
    api.get<UserSubscriptions>(`/admin/users/${encodeURIComponent(id)}/subscriptions`),
  /** Give a paid tier nobody bought. `endsAt` null means no end date. */
  grantSubscription: (id: string, body: { appSlug: string; tier: string; endsAt: string | null; reason: string }) =>
    api.post<ResolvedSubscription>(`/admin/users/${encodeURIComponent(id)}/subscriptions/grant`, body),
  endSubscriptionGrant: (id: string, body: { appSlug: string; reason: string }) =>
    api.post<ResolvedSubscription>(`/admin/users/${encodeURIComponent(id)}/subscriptions/end-grant`, body),

  /** The Subscriptions section (org-admin#162). */
  subscriptionOverview: () => api.get<SubscriptionOverview>('/admin/subscriptions/overview'),
  subscriptionGrants: (filter: { status: 'active' | 'ended' | 'all'; app?: string; endingWithinDays?: number }) =>
    api.get<{ grants: PromotionalGrantRow[] }>(
      `/admin/subscriptions/grants${q({
        status: filter.status,
        ...(filter.app ? { app: filter.app } : {}),
        ...(filter.endingWithinDays ? { endingWithinDays: filter.endingWithinDays } : {}),
      })}`,
    ),
  subscriptionEvents: (filter: { app?: string; source?: string; before?: string }) =>
    api.get<{ events: SubscriptionEventRow[]; next: string | null }>(
      `/admin/subscriptions/events${q({
        ...(filter.app ? { app: filter.app } : {}),
        ...(filter.source ? { source: filter.source } : {}),
        ...(filter.before ? { before: filter.before } : {}),
      })}`,
    ),

  // ── App roles ──────────────────────────────────────────────────────
  appRoles: (applicationId: string) =>
    api.get<{ roles: AppRoleRow[] }>(
      `/admin/applications/${encodeURIComponent(applicationId)}/roles`,
    ),
  createAppRole: (
    applicationId: string,
    body: { name: string; label: string; description?: string },
  ) =>
    api.post<AppRoleRow>(`/admin/applications/${encodeURIComponent(applicationId)}/roles`, body),
  /** Label and description only. There is no rename: `name` is what a consuming
   *  service matches on, as a bare string with no foreign key anywhere. */
  updateAppRole: (id: string, body: { label?: string; description?: string }) =>
    api.patch<AppRoleRow>(`/admin/app-roles/${encodeURIComponent(id)}`, body),
  deleteAppRole: (id: string) =>
    api.del<{ id: string; deleted: true; users: number; groups: number }>(
      `/admin/app-roles/${encodeURIComponent(id)}`,
    ),
  /** The COMPLETE set of app-role IDS held directly, across every application.
   *  Ids rather than names, because a name is unique only within its app. */
  setUserAppRoles: (id: string, appRoleIds: string[]) =>
    api.put<{ id: string; appRoleIds: string[] }>(
      `/admin/users/${encodeURIComponent(id)}/app-roles`,
      { appRoleIds },
    ),
  setSecurityGroupAppRoles: (slug: string, appRoleIds: string[]) =>
    api.put<{ slug: string; appRoleIds: string[] }>(
      `/admin/security-groups/${encodeURIComponent(slug)}/app-roles`,
      { appRoleIds },
    ),
  /**
   * The COMPLETE set of applications granted DIRECTLY to this account — not the
   * set they can reach. What a group entitles is not in this table and is not
   * touched, so sending back everything `userAccess` reported would convert
   * every inherited entitlement into a direct one.
   */
  setUserApplications: (id: string, applications: string[]) =>
    api.put<{ id: string; applications: string[] }>(
      `/admin/users/${encodeURIComponent(id)}/applications`,
      { applications },
    ),
  addSecurityGroupMembers: (slug: string, userIds: string[]) =>
    api.post<{ slug: string; added: string[]; alreadyMembers: string[] }>(
      `/admin/security-groups/${encodeURIComponent(slug)}/members`,
      { userIds },
    ),
  removeSecurityGroupMember: (slug: string, userId: string) =>
    api.del<{ slug: string; userId: string; removed: boolean }>(
      `/admin/security-groups/${encodeURIComponent(slug)}/members/${encodeURIComponent(userId)}`,
    ),
  createSecurityGroup: (body: { slug: string; name: string; description?: string }) =>
    api.post<SecurityGroupDetail>('/admin/security-groups', body),
  updateSecurityGroup: (slug: string, body: { name?: string; description?: string }) =>
    api.patch<{ slug: string; name: string; description: string }>(
      `/admin/security-groups/${encodeURIComponent(slug)}`,
      body,
    ),
  deleteSecurityGroup: (slug: string) =>
    api.del<{ slug: string; deleted: true; affectedUsers: number }>(
      `/admin/security-groups/${encodeURIComponent(slug)}`,
    ),
  /** The COMPLETE set, not a delta — same shape and same reason as the
   *  application grants above. */
  setSecurityGroupRoles: (slug: string, roles: string[]) =>
    api.put<{ slug: string; roles: string[] }>(
      `/admin/security-groups/${encodeURIComponent(slug)}/roles`,
      { roles },
    ),
  setSecurityGroupApplications: (slug: string, applications: string[]) =>
    api.put<{ slug: string; applications: string[] }>(
      `/admin/security-groups/${encodeURIComponent(slug)}/applications`,
      { applications },
    ),

  // ── Credentials ────────────────────────────────────────────────────
  /**
   * Ours, NOT the plugins' own listings.
   *
   * `/oauth2/get-clients` returns clients whose `userId` is the caller's, and
   * `/api-key/list` returns the caller's own keys. Both would render here as a
   * short list with nothing erroring — indistinguishable from a complete one.
   * See the auth service's plugins/admin-console.ts.
   */
  credentials: (opts: { limit?: number; terms?: string[] } = {}) =>
    api.get<Credentials & { terms: string[] }>(
      `/admin/credentials${q({ limit: opts.limit ?? 100, q: opts.terms })}`,
    ),
  apiKey: (id: string) => api.get<ApiKeyDetail>(`/admin/credentials/api-keys/${encodeURIComponent(id)}`),
  revokeApiKey: (id: string) =>
    api.post<{ id: string; enabled: false; alreadyRevoked: boolean }>(
      `/admin/credentials/api-keys/${id}/revoke`,
    ),
  /**
   * A new secret for the SAME credential (RidgeWill/auth#541). Grants, app,
   * agent marker, role and expiry are untouched. The old secret keeps working
   * until `previousSecretValidUntil`, twelve hours on (RidgeWill/auth#649).
   * The new one is readable in this response and never again.
   */
  rotateApiKey: (id: string) =>
    api.post<{ id: string; key: string; start: string | null; previousSecretValidUntil?: string }>(
      `/admin/credentials/api-keys/${encodeURIComponent(id)}/rotate`,
    ),
  /**
   * Name, expiry (days from now, or null for never) and role (null removes it).
   * Only the fields sent change (RidgeWill/auth#547).
   */
  updateApiKey: (
    id: string,
    body: { name?: string; expiresInDays?: number | null; role?: string | null },
  ) =>
    api.patch<{ id: string; name: string | null; expiresAt: string | null; role: string | null; unchanged: boolean }>(
      `/admin/credentials/api-keys/${encodeURIComponent(id)}`,
      body,
    ),
  /** The COMPLETE set afterwards, not a delta. In force on the key's next request. */
  setApiKeyGrants: (id: string, grants: CredentialGrant[]) =>
    api.put<{ id: string; grants: CredentialGrant[]; unchanged: boolean }>(
      `/admin/credentials/api-keys/${id}/grants`,
      { grants },
    ),

  /**
   * Issue a service credential. The ONE admin call that is not under
   * `/api/auth`, and the reason is worth stating rather than discovering.
   *
   * Better Auth's `/api-key/create` refuses `permissions` from anything carrying
   * a request or headers — it is a server-only property. So a key minted over
   * HTTP can never hold one, and a key without one is refused by every route
   * that demands it. The auth service therefore issues from an Express route
   * that calls `createApiKey` in-process, which is outside the handler's base
   * path by necessity: everything under `/api/auth` belongs to Better Auth.
   *
   * `rootApi` rather than `api` for that reason alone. Anything else admin-side
   * should still go through `api`.
   */
  issueServiceCredential: (input: IssueCredentialInput) =>
    rootApi.post<IssuedCredential>('/admin/service-credentials', input),
  /**
   * The same, filed under an existing AGENT rather than the caller
   * (RidgeWill/auth#544). Always agent-marked; its role must be one the agent
   * holds as well as one the issuer may grant.
   */
  issueAgentCredential: (agentId: string, input: Omit<IssueCredentialInput, 'agent'>) =>
    rootApi.post<IssuedCredential>(
      `/admin/agents/${encodeURIComponent(agentId)}/service-credentials`,
      input,
    ),

  /** Creates the account, marks it an agent, sets its role and optionally mints
   *  its credential, in one call. See RidgeWill/auth#538 for why all three. */
  createAgent: (input: CreateAgentInput) => rootApi.post<CreatedAgent>('/admin/agents', input),

  // ── Notifications (RidgeWill/auth#508, org-admin#131) ──────────────
  /**
   * Express routes on the auth service, like credential issuing, so `rootApi`.
   * They are served only by the admin deployment (`servesNotificationAdmin`).
   *
   * A 404 from any of them means one of three things the portal cannot tell
   * apart: the role lacks the permission, the id does not exist, or the auth
   * service predates #508. The screens say so rather than passing it through
   * `describe()`, whose 404 sentence is about the admin role as a whole.
   */
  notificationSummary: () => rootApi.get<NotificationSummary>('/admin/notifications/summary'),

  notificationSends: (filter: SendFilter = {}) =>
    rootApi.get<Paged<NotificationSend>>(
      `/admin/notifications/sends${q({
        address: filter.address,
        app: filter.app,
        type: filter.type,
        category: filter.category,
        status: filter.status,
        delivery: filter.delivery,
        from: filter.from,
        to: filter.to,
        limit: filter.limit,
        cursor: filter.cursor,
      })}`,
    ),

  notificationSend: (id: string) =>
    rootApi.get<NotificationSendDetail>(`/admin/notifications/sends/${encodeURIComponent(id)}`),

  notificationOptOuts: (filter: OptOutFilter = {}) =>
    rootApi.get<Paged<NotificationOptOut>>(
      `/admin/notifications/opt-outs${q({
        address: filter.address,
        app: filter.app,
        source: filter.source,
        limit: filter.limit,
        cursor: filter.cursor,
      })}`,
    ),

  // ── Group email (RidgeWill/auth routes/admin-group-email.ts) ───────
  /**
   * Drafts, schedules and sends to security groups and typed addresses. The
   * list needs `notification:read`; everything else `notification:send`.
   */
  groupEmails: () => rootApi.get<{ items: GroupEmail[] }>('/admin/notifications/group-emails'),
  groupEmail: (id: string) => rootApi.get<GroupEmail>(`/admin/notifications/group-emails/${encodeURIComponent(id)}`),
  groupEmailOptions: () => rootApi.get<GroupEmailOptions>('/admin/notifications/group-email-groups'),
  createGroupEmail: (fields: GroupEmailFields) => rootApi.post<GroupEmail>('/admin/notifications/group-emails', fields),
  updateGroupEmail: (id: string, fields: Partial<GroupEmailFields>) =>
    rootApi.patch<GroupEmail>(`/admin/notifications/group-emails/${encodeURIComponent(id)}`, fields),
  deleteGroupEmail: (id: string) => rootApi.del(`/admin/notifications/group-emails/${encodeURIComponent(id)}`),
  scheduleGroupEmail: (id: string, scheduledAt: string) =>
    rootApi.post<GroupEmail>(`/admin/notifications/group-emails/${encodeURIComponent(id)}/schedule`, { scheduledAt }),
  unscheduleGroupEmail: (id: string) =>
    rootApi.post<GroupEmail>(`/admin/notifications/group-emails/${encodeURIComponent(id)}/unschedule`),
  sendGroupEmail: (id: string) =>
    rootApi.post<GroupEmail & { queued: number; notQueued: number }>(
      `/admin/notifications/group-emails/${encodeURIComponent(id)}/send`,
    ),
  testGroupEmail: (id: string) =>
    rootApi.post<{ to: string }>(`/admin/notifications/group-emails/${encodeURIComponent(id)}/test`),
  previewGroupEmail: (fields: GroupEmailFields) =>
    rootApi.post<{ subject: string; html: string }>('/admin/notifications/group-emails/preview', fields),
  groupEmailRecipients: (fields: Pick<GroupEmailFields, 'groupIds' | 'addresses'>) =>
    rootApi.post<RecipientCount>('/admin/notifications/group-emails/recipients', fields),
};

export type GroupEmailTemplate = 'ridgewill' | 'keyodo';
export type GroupEmailKind = 'announcement' | 'notice';
export type GroupEmailStatus = 'draft' | 'scheduled' | 'sending' | 'sent';

/** What the editor saves. */
export type GroupEmailFields = {
  template: GroupEmailTemplate;
  kind: GroupEmailKind;
  subject: string;
  preheader: string;
  bodyHtml: string;
  groupIds: string[];
  addresses: string[];
};

export type GroupEmail = Omit<GroupEmailFields, 'groupIds'> & {
  id: string;
  status: GroupEmailStatus | (string & {});
  /** `name` is null when the group has since been deleted. */
  groups: { id: string; name: string | null }[];
  scheduledAt: string | null;
  sentAt: string | null;
  recipientCount: number | null;
  createdAt: string;
  updatedAt: string;
  createdBy: string | null;
  updatedBy: string | null;
  sentBy: string | null;
  /** What became of the copies, once it has been sent. */
  outcomes: {
    queued: number;
    sent: number;
    delivered: number;
    bounced: number;
    suppressed: number;
    refused: number;
    failed: number;
  } | null;
};

export type GroupEmailOptions = {
  items: { id: string; name: string; members: number }[];
  mergeFields: { token: string; description: string }[];
};

export type RecipientCount = {
  total: number;
  fromGroups: number;
  typed: number;
  duplicates: number;
  invalid: string[];
  missingGroups: string[];
  /** Null when the role cannot read the opt-out register. */
  optedOut: number | null;
};

/**
 * One page of a cursor-paged list. No total: the Notifications routes page by
 * `(createdAt, id)` and never count, because a count over the send log is the
 * expensive query the cursor exists to avoid.
 */
export type Paged<T> = { items: T[]; nextCursor: string | null };

/** The values auth's route validates against (`SEND_STATUSES`,
 *  `DELIVERY_STATUSES`). A value outside these is a 400, not an empty page. */
export type SendStatus = 'queued' | 'sending' | 'sent' | 'suppressed' | 'refused' | 'failed';
export type DeliveryStatus = 'delayed' | 'delivered' | 'failed' | 'suppressed' | 'bounced' | 'complained';
export type SendCategory = 'security' | 'account' | 'invitation' | 'reminder' | 'announcement';
export type OptOutSource = 'one-click' | 'email-link' | 'form' | 'complaint';

export type SendFilter = {
  address?: string;
  app?: string;
  type?: string;
  category?: SendCategory;
  status?: SendStatus;
  delivery?: DeliveryStatus;
  /** ISO, inclusive. */
  from?: string;
  /** ISO, exclusive. */
  to?: string;
  limit?: number;
  cursor?: string;
};

export type OptOutFilter = {
  address?: string;
  app?: string;
  source?: OptOutSource;
  limit?: number;
  cursor?: string;
};

/**
 * A send as the list returns it. Never any content: the service does not keep
 * a message once it is sent, and the route does not select what it has.
 *
 * `status` and `deliveryStatus` are typed as the known values OR a string,
 * because auth owns the vocabulary and a value added there must still render.
 */
export type NotificationSend = {
  id: string;
  channel: string;
  appSlug: string;
  type: string;
  category: string;
  recipient: string;
  status: SendStatus | (string & {});
  /** Why a send was refused, suppressed or failed. Null otherwise. */
  reason: string | null;
  /** The most informative provider result so far, or null before one arrives. */
  deliveryStatus: DeliveryStatus | (string & {}) | null;
  createdAt: string;
  /** Null while queued or sending. */
  decidedAt: string | null;
};

export type NotificationSendDetail = NotificationSend & {
  idempotencyKey: string | null;
  attempts: number;
  nextAttemptAt: string | null;
  lastError: string | null;
  providerMessageId: string | null;
  deliveryDetail: unknown;
  deliveryUpdatedAt: string | null;
};

export type NotificationOptOut = {
  id: string;
  channel: string;
  address: string;
  source: OptOutSource | (string & {});
  appSlug: string | null;
  sendId: string | null;
  /** The evidence. All three are nulled 24 months on, and `evidenceClearedAt` says when. */
  ipAddress: string | null;
  country: string | null;
  userAgent: string | null;
  evidenceClearedAt: string | null;
  createdAt: string;
};

export type OutcomeCounts = { sent: number; suppressed: number; refused: number; failed: number };

export type NotificationSummary = {
  generatedAt: string;
  queue: {
    queued: number;
    sending: number;
    oldestQueuedAt: string | null;
    oldestQueuedAgeSeconds: number | null;
  };
  last24h: OutcomeCounts;
  last7d: OutcomeCounts;
  /** Sends with status `sent` in the last 7 days, per app. Rates are fractions. */
  deliverability: {
    app: string;
    sent: number;
    bounced: number;
    complained: number;
    bounceRate: number;
    complaintRate: number;
  }[];
};

/**
 * The grants the auth service lets a credential carry, restated. Its
 * `GRANTABLE` (services/auth/src/lib/service-credentials.ts) is the source; a
 * grant missing here cannot be issued from the portal at all.
 * `subscriptions:read` is RidgeWill/auth#495, `notifications:send` is
 * RidgeWill/auth#504, `applications:read` is RidgeWill/auth#564, and
 * `events:publish` is RidgeWill/keyodo#562 (support announcing a reply).
 *
 * `connections:declare` was missing here long after the service offered it, which
 * mattered little while keys could only be issued. It matters now: editing
 * replaces the whole set, so a grant the portal cannot name would be dropped
 * from every key it is saved on.
 */
export type CredentialGrant =
  | 'users:lookup'
  | 'users:read'
  | 'connections:declare'
  | 'subscriptions:read'
  | 'notifications:send'
  | 'applications:read'
  | 'events:publish'
  | 'groups:read'
  | 'staff:read';

export type CreateAgentInput = {
  name: string;
  /**
   * The agent's address. It IDENTIFIES the account and nothing more: an agent
   * cannot sign in and cannot be recovered through it (RidgeWill/auth#539), so
   * this is a label rather than a way in.
   */
  email: string;
  role?: string;
  /** Omitted entirely when no key is wanted. An agent with no credential is a
   *  real state, so the server refuses an EMPTY object and accepts an absent
   *  one. */
  credential?: {
    name: string;
    appSlug: string;
    grants: CredentialGrant[];
    expiresInDays?: number;
  };
};

export type CreatedAgent = {
  userId: string;
  email: string;
  name: string;
  role: string | null;
  isAgent: true;
  /** Readable exactly once, and null when none was asked for. */
  credential: { id: string; key: string; prefix: string | null; start: string | null } | null;
  /**
   * Set when the ACCOUNT was created and the credential was not. Both halves
   * are reported because the account is the part that cannot be undone — the
   * key can be issued again from the credentials screen.
   */
  credentialError: { code: string; message: string } | null;
};

export type IssueCredentialInput = {
  name: string;
  appSlug: string;
  grants: CredentialGrant[];
  expiresInDays?: number;
  /**
   * Marks the credential as an automated agent's (RidgeWill/platform#31).
   * Attribution only: it grants nothing and withholds nothing, but every action
   * taken with the key is then filterable as an agent's rather than a service's.
   */
  agent?: boolean;
  /**
   * The role the credential acts with, which ordinary service credentials do
   * not have. The auth service refuses a role the ISSUER does not hold, so this
   * screen offers only the signed-in administrator's own.
   */
  role?: string;
};

export type IssuedCredential = {
  id: string;
  /** Readable exactly once. Keys are hashed at rest; there is no second look. */
  key: string;
  prefix: string | null;
  start: string | null;
  appSlug: string;
  grants: CredentialGrant[];
  expiresInDays: number | null;
  agent: boolean;
  role: string | null;
};

/** One tester card, as `GET /admin/testers` returns it (auth lib/tester-cards.ts). */
export type TesterCardRow = {
  id: string;
  userId: string;
  name: string;
  email: string;
  image: string | null;
  apps: string[];
  answers: {
    experience?: string;
    feedback?: string;
    time?: string;
    inviteEmail?: string;
    accessibility?: string[];
    agreeChanges?: boolean;
    agreePrivate?: boolean;
  };
  submittedAt: string | null;
  createdAt: string;
  updatedAt: string;
  devices: TesterDevice[];
};
